import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Settings2, Plus } from 'lucide-react';
import { UserPhoto } from '../../../components';
import {
  CARD_TONES, carerNames, initials, periodOf, toMinutes, fromMinutes,
  weekdayShort, rotaLabel, PRIORITY_BADGE, groupVisits, visitStatus,
} from './shared';

/**
 * The planner's timeline: one row per resident (or per carer), one column
 * per time of day, hour, or day of the week.
 *
 * Purely a view of what it is handed — the page decides which tasks and rows
 * are shown (filters, search, groups) and owns every dialog. The grid only
 * reports clicks: open a task, add care in a cell, drop a task on a carer.
 *
 * A cell shows at most CELL_LIMIT cards, most urgent first, then "View all":
 * a busy morning for one resident should not stretch the whole row.
 */

const CELL_LIMIT = 3;

/** [start, end) minute ranges on 0..1440 for a span that may wrap midnight. */
const ranges = (start, end) => (start < end ? [[start, end]] : [[start, 1440], [0, end]]);

/** Where a carer's rostered hours meet a column, as "07:00–12:00" pieces. */
const workingPieces = (entry, spanStart, spanEnd) => {
  if (!entry || !entry.on) return [];
  const rs = toMinutes(entry.start);
  const re = toMinutes(entry.end);
  if (rs === null || re === null) return [];
  const pieces = [];
  ranges(spanStart, spanEnd).forEach(([a, b]) => ranges(rs, re).forEach(([c, d]) => {
    const s = Math.max(a, c);
    const e = Math.min(b, d);
    if (e > s) pieces.push(`${fromMinutes(s)}–${fromMinutes(e === 1440 ? 0 : e)}`);
  }));
  return pieces;
};

// Most pressing first: missed, then overdue, then everything else by time.
const cellOrder = (v) => ({ missed: 0, overdue: 1, unassigned: 2 }[v.tone] ?? 3);

const Avatar = ({ name, photo, tone = 'blue' }) => (
  <UserPhoto
    src={photo}
    alt=""
    className="w-8 h-8 rounded-full"
    fallback={(
      <span className={`w-8 h-8 rounded-full grid place-items-center text-[10.5px] font-extrabold shrink-0 ${
        tone === 'purple'
          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
          : 'bg-[#DCE8FF] text-[#1E40AF] dark:bg-blue-900/30 dark:text-blue-300'
      }`}
      >
        {initials(name)}
      </span>
    )}
  />
);

/**
 * One care visit: everything planned together for this resident at this
 * time. A single task reads as that task; several are listed by name, so a
 * visit with "Repositioning, Washing & bathing, Hearing aid support" shows all
 * three rather than hiding two behind "View all".
 */
export const VisitCard = ({ visit, mode, onOpen, draggable = false, compact = false }) => {
  const task = visit.first;
  const status = visitStatus(visit);
  const priority = PRIORITY_BADGE[visit.priority];
  const who = mode === 'staff'
    ? task.resident?.name
    : carerNames(task) || 'Unassigned';
  const many = visit.tasks.length > 1;

  return (
    <button
      type="button"
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', visit.key);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={() => onOpen(visit)}
      title={`${visit.labels.join(', ')} · ${task.start_time}–${task.end_time}`}
      className={`block w-full text-left rounded-[8px] border border-l-[4px] px-2.5 ${compact ? 'py-1.5' : 'py-2'} transition-shadow hover:shadow-[0_2px_10px_rgba(15,23,42,0.10)] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 ${
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      } ${CARD_TONES[visit.tone]}`}
    >
      <div className="flex items-start justify-between gap-1.5">
        <strong className="text-[12px] font-bold leading-snug">{many ? `${visit.tasks.length} care tasks` : task.action_label}</strong>
        {priority && (
          <span
            className={`mt-[3px] w-2 h-2 rounded-full shrink-0 ${visit.priority === 'urgent' ? 'bg-rose-500' : 'bg-amber-500'}`}
            title={priority.label}
            aria-label={priority.label}
          />
        )}
      </div>
      {many && (
        <ul className="mt-0.5 text-[10.5px] font-semibold leading-snug">
          {visit.tasks.map((t) => (
            <li key={t.id} className={`truncate ${!t.open ? 'line-through opacity-70' : ''}`}>· {t.action_label}</li>
          ))}
        </ul>
      )}
      {status && <div className="text-[10.5px] font-bold mt-0.5">{status}</div>}
      <div className="text-[10.5px] mt-1 tabular-nums opacity-90">{task.start_time}–{task.end_time}</div>
      <div className="text-[10.5px] mt-0.5 opacity-90 truncate">{who}</div>
      {task.staff_required === 2 && <div className="text-[10.5px] mt-0.5 opacity-80">2 staff required</div>}
      {visit.late && <div className="text-[10px] font-bold mt-1">Late</div>}
      {visit.needs_review && <div className="text-[9.5px] font-extrabold uppercase tracking-[0.06em] mt-1">Needs review</div>}
    </button>
  );
};

const PlannerGrid = ({
  mode,                // 'resident' | 'staff'
  view,                // 'day' | 'week'
  hourly,
  dates,
  sections,            // [{ key, group|null, rows: [{ id, name, sub, photo }] }]
  tasks,
  periods,
  rota,
  today,
  canAdd,
  collapsed,
  onToggleGroup,
  onEditGroup,
  onAdd,
  onOpenVisit,
  onOpenDay,
  onDropTask,
  onViewAll,
}) => {
  const [dragOver, setDragOver] = useState(null);

  const columns = useMemo(() => {
    if (view === 'week') {
      return dates.map((date) => ({
        key: date,
        label: `${weekdayShort(date)} ${Number(date.slice(8, 10))}`,
        date,
        time: '09:00',
        span: [0, 1440],
        isToday: date === today,
        match: (t) => t.task_date === date,
      }));
    }
    const date = dates[0];
    if (hourly) {
      return Array.from({ length: 24 }, (_, h) => {
        const hh = String(h).padStart(2, '0');
        return { key: hh, label: `${hh}:00`, date, time: `${hh}:00`, span: [h * 60, (h + 1) * 60], match: (t) => t.start_time.slice(0, 2) === hh };
      });
    }
    return periods.map((p) => ({
      key: p.key,
      label: p.label,
      date,
      time: fromMinutes(p.start),
      span: [p.start, p.end],
      match: (t) => periodOf(t.start_time, periods) === p.key,
    }));
  }, [view, hourly, dates, periods, today]);

  const minCol = view === 'week' ? 140 : hourly ? 128 : 170;
  const rowHead = 150;
  const template = { gridTemplateColumns: `${rowHead}px repeat(${columns.length}, minmax(${minCol}px, 1fr))`, minWidth: rowHead + (columns.length * minCol) };

  const rowTasks = (row) => (mode === 'resident'
    ? tasks.filter((t) => Number(t.residence_id) === Number(row.id))
    : tasks.filter((t) => Number(t.assigned_to) === Number(row.id) || Number(t.second_assignee) === Number(row.id)));

  const renderStaffRota = (row, column) => {
    const entry = rota?.[row.id]?.[column.date];
    if (entry === undefined || entry === null) return { label: null, off: false };
    if (entry.absence && !entry.absence.from) return { label: `Unavailable · ${entry.absence.reason || 'Leave'}`, off: true };
    if (view === 'week') return { label: rotaLabel(entry), off: !entry.on };
    const pieces = workingPieces(entry, column.span[0], column.span[1]);
    if (!pieces.length) return { label: 'Off duty', off: true };
    return { label: `Working ${pieces.join(', ')}`, off: false };
  };

  const renderCell = (row, column) => {
    const items = groupVisits(rowTasks(row).filter(column.match))
      .sort((a, b) => cellOrder(a) - cellOrder(b) || a.first.start_time.localeCompare(b.first.start_time));
    const shown = items.slice(0, CELL_LIMIT);
    const cellKey = `${row.id}|${column.key}`;
    const staffRota = mode === 'staff' ? renderStaffRota(row, column) : { label: null, off: false };
    const addable = canAdd && mode === 'resident' && column.date >= today;
    const isDropTarget = mode === 'staff' && onDropTask;

    return (
      <div
        key={cellKey}
        onDragOver={isDropTarget ? (e) => { e.preventDefault(); setDragOver(cellKey); } : undefined}
        onDragLeave={isDropTarget ? () => setDragOver((k) => (k === cellKey ? null : k)) : undefined}
        onDrop={isDropTarget ? (e) => {
          e.preventDefault();
          setDragOver(null);
          const key = e.dataTransfer.getData('text/plain');
          if (key) onDropTask(key, row);
        } : undefined}
        className={`relative min-h-[96px] p-1.5 border-b border-r border-[#E8EDF5] dark:border-gray-800 flex flex-col gap-1.5 ${
          dragOver === cellKey
            ? 'bg-blue-50 dark:bg-blue-950/30 outline outline-2 -outline-offset-2 outline-blue-400'
            : staffRota.off
              ? 'bg-[repeating-linear-gradient(135deg,#F3F5F8_0,#F3F5F8_6px,#EBEEF3_6px,#EBEEF3_12px)] dark:bg-[repeating-linear-gradient(135deg,#22262d_0,#22262d_6px,#1f232a_6px,#1f232a_12px)]'
              : column.isToday ? 'bg-[#FAFCFF] dark:bg-[#272d36]' : 'bg-white dark:bg-[#262b33]'
        }`}
      >
        {staffRota.label && (
          <span className="text-[10px] font-semibold text-[#8A96AA] dark:text-gray-500 leading-tight px-0.5">{staffRota.label}</span>
        )}
        {shown.map((visit) => (
          <VisitCard
            key={visit.key}
            visit={visit}
            mode={mode}
            onOpen={onOpenVisit}
            draggable={mode === 'staff' && visit.open.length > 0 && !!onDropTask}
          />
        ))}
        {items.length > CELL_LIMIT && (
          <button
            type="button"
            onClick={() => onViewAll(row, column, items)}
            className="text-[11px] font-bold text-blue-600 dark:text-blue-300 hover:underline text-left px-1"
          >
            View all {items.length}
          </button>
        )}
        {addable && (
          <button
            type="button"
            onClick={() => onAdd(row, column.date, column.time)}
            aria-label={`Plan care for ${row.name} · ${column.label}`}
            className="mt-auto self-start w-7 h-6 grid place-items-center rounded-md border border-dashed border-[#CBD6E6] dark:border-gray-700 text-[#6B86B8] dark:text-gray-400 hover:bg-[#EEF3FF] hover:border-blue-300 hover:text-blue-600 dark:hover:bg-blue-900/20 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  const renderRow = (row) => {
    const entryToday = mode === 'staff' && view === 'day' ? rota?.[row.id]?.[dates[0]] : null;
    const chip = mode === 'staff' && view === 'day' ? rotaLabel(entryToday) : null;
    return (
      <React.Fragment key={`row-${row.id}`}>
        <div className="sticky left-0 z-[2] bg-white dark:bg-[#262b33] border-b border-r border-[#E8EDF5] dark:border-gray-800 flex flex-col items-center justify-center text-center px-2 py-3 gap-1 min-w-0">
          <Avatar name={row.name} photo={row.photo} />
          <strong className="text-[12px] font-semibold text-[#0B1220] dark:text-gray-100 leading-tight break-words max-w-full">{row.name}</strong>
          {row.sub && <small className="text-[10.5px] text-[#94A3B8] dark:text-gray-500 leading-tight">{row.sub}</small>}
          {chip && (
            <span className={`mt-0.5 text-[10.5px] font-semibold px-2 py-0.5 rounded-md tabular-nums ${
              entryToday?.on && !entryToday?.absence ? 'bg-[#EEF3FF] text-blue-600 dark:bg-blue-900/20 dark:text-blue-300' : 'bg-[#F1F5F9] text-[#64748B] dark:bg-gray-800 dark:text-gray-400'
            }`}
            >
              {chip}
            </span>
          )}
        </div>
        {columns.map((column) => renderCell(row, column))}
      </React.Fragment>
    );
  };

  return (
    <div className="overflow-auto max-h-[calc(100vh-230px)] min-h-[320px] rounded-b-[16px]">
      <div className="grid" style={template}>
        {/* Header */}
        <div className="sticky top-0 left-0 z-[5] bg-[#F7F9FC] dark:bg-[#20242c] border-b border-r border-[#E5EAF2] dark:border-gray-800 px-3 py-3 text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#8490A6] dark:text-gray-500 flex items-center justify-center text-center">
          {mode === 'resident' ? 'Resident / room' : 'Carer'}
        </div>
        {columns.map((column) => (
          <div
            key={`h-${column.key}`}
            className={`sticky top-0 z-[3] border-b border-[#E5EAF2] dark:border-gray-800 px-3 py-3 text-[11.5px] font-bold ${
              column.isToday ? 'bg-[#EEF3FF] text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' : 'bg-[#F7F9FC] text-[#64748B] dark:bg-[#20242c] dark:text-gray-400'
            }`}
          >
            {view === 'week' ? (
              <button type="button" onClick={() => onOpenDay(column.date)} className="hover:text-blue-600 dark:hover:text-blue-300" title="Open this day">
                {column.label}
              </button>
            ) : column.label}
          </div>
        ))}

        {sections.map((section) => {
          if (!section.group) return section.rows.map(renderRow);
          const isCollapsed = collapsed.has(section.key);
          const count = section.rows.reduce((n, row) => n + rowTasks(row).length, 0);
          return (
            <React.Fragment key={`g-${section.key}`}>
              <div className="border-b border-[#E5EAF2] dark:border-gray-800 bg-[#F8FAFE] dark:bg-[#22272e]" style={{ gridColumn: '1 / -1' }}>
                <div className="sticky left-0 w-fit flex items-center gap-2.5 px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => onToggleGroup(section.key)}
                    aria-expanded={!isCollapsed}
                    aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${section.group.name}`}
                    className="w-7 h-7 grid place-items-center rounded-lg border border-[#DCE4EF] dark:border-gray-700 bg-white dark:bg-[#262b33] text-[#64748B] hover:text-blue-600"
                  >
                    {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: section.group.color || '#94A3B8' }} />
                  <span className="text-[12.5px] font-extrabold tracking-[0.08em] uppercase text-[#0B1220] dark:text-gray-100">{section.group.name}</span>
                  {section.group.id && onEditGroup && (
                    <button
                      type="button"
                      onClick={() => onEditGroup(section.group)}
                      aria-label={`Edit ${section.group.name}`}
                      className="w-7 h-7 grid place-items-center rounded-lg border border-[#DCE4EF] dark:border-gray-700 bg-white dark:bg-[#262b33] text-[#64748B] hover:text-blue-600"
                    >
                      <Settings2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 bg-[#EEF3FF] dark:bg-blue-900/20 rounded-md px-2 py-1">
                    {section.rows.length} resident{section.rows.length === 1 ? '' : 's'}
                  </span>
                  <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 bg-[#EEF3FF] dark:bg-blue-900/20 rounded-md px-2 py-1">
                    {count} care action{count === 1 ? '' : 's'}
                  </span>
                </div>
              </div>
              {!isCollapsed && section.rows.map(renderRow)}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default PlannerGrid;
